import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { editIcon, Tooltip } from '@tpct/admin-ui';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import FeatureGateTooltip from '@/hub-shared/components/FeatureGateTooltip';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalGateDecision } from '../gates/portalGates';
import type { PortalBootstrapData } from '../types/window';
import type { RoleTemplate, RoleTemplatesResponse } from '../types/role-templates';
import type { PortalSearchSyncFn } from '../utils/portalUrlState';
import { parsePortalSearchParams } from '../utils/portalUrlState';
import RolePermissionEditor from './RolePermissionEditor';
import { ROLE_DISPLAY_NAMES, ROLE_KEY_TO_WP_SLUG } from './permissionCatalog';
import { getCabinetRolesTemplateEditGate } from '../gates/portalGates';
import { parseApiError } from '../utils/parseApiError';

type CabinetRolesTabProps = {
  bootstrap: PortalBootstrapData;
  cabinetId: number;
  search?: string;
  patchPortalSearch?: PortalSearchSyncFn;
};

const fetchJson = async <T,>(url: string, nonce: string): Promise<T> => {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': nonce },
  });
  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }
  return response.json() as Promise<T>;
};

type RolesTableProps = {
  templates: RoleTemplate[];
  counts: Record<string, number>;
  templateEditGate: PortalGateDecision;
  onEdit: (roleKey: string) => void;
};

const RolesTable = ({ templates, counts, templateEditGate, onEdit }: RolesTableProps) => {
  const canEditTemplates = templateEditGate.allowed;
  const displayNames = useMemo(() => ROLE_DISPLAY_NAMES(), []);

  return (
    <div className="container-style">
      <table className="thn-table roles-table">
        <thead>
          <tr>
            <th scope="col">{__('Rôles')}</th>
            <th scope="col">{__('Utilisateurs')}</th>
            <th scope="col" className="roles-table__actions-header">{__('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => {
            const wpSlug = ROLE_KEY_TO_WP_SLUG[template.role_key] ?? template.role_key;
            const count = counts[wpSlug] ?? 0;
            const label = displayNames[template.role_key] ?? template.role_key;
            const userCountLabel = template.role_key === 'mission_responsable' ? __('Par mission') : count;

            return (
              <tr key={template.role_key}>
                <td className="roles-table__role-cell">
                  <span className="roles-table__role-text" title={label}>
                    {label}
                  </span>
                </td>
                <td>{userCountLabel}</td>
                <td className="roles-table__actions-cell">
                  {template.read_only ? (
                    <Tooltip text={__('Vous ne pouvez pas éditer ce rôle')} position="top">
                      <button
                        type="button"
                        className="roles-table__action-btn roles-table__action-btn--disabled"
                        disabled
                        aria-disabled="true"
                      >
                        <img src={editIcon} alt="" width={14} height={14} aria-hidden />
                        {__('Éditer le rôle')}
                      </button>
                    </Tooltip>
                  ) : (
                    <FeatureGateTooltip decision={templateEditGate}>
                      <button
                        type="button"
                        className="roles-table__action-btn"
                        onClick={() => onEdit(template.role_key)}
                        disabled={!canEditTemplates}
                      >
                        <img src={editIcon} alt="" width={14} height={14} aria-hidden />
                        {__('Éditer le rôle')}
                      </button>
                    </FeatureGateTooltip>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CabinetRolesTab = ({ bootstrap, cabinetId, search, patchPortalSearch }: CabinetRolesTabProps) => {
  const params = useMemo(() => parsePortalSearchParams(search ?? ''), [search]);
  const editingRoleKey = params.get('editRole') ?? null;

  const [localTemplates, setLocalTemplates] = useState<RoleTemplate[] | null>(null);

  const baseUrl = bootstrap.restUrls.accountingHub;
  const nonce = bootstrap.nonce;
  const templateEditGate = getCabinetRolesTemplateEditGate(bootstrap);

  const templatesQuery = useQuery<RoleTemplatesResponse>({
    queryKey: ['role-templates', cabinetId],
    queryFn: () => fetchJson<RoleTemplatesResponse>(`${baseUrl}user-management/role-templates`, nonce),
  });

  const templates = localTemplates ?? templatesQuery.data?.data.roles ?? null;
  const counts = templatesQuery.data?.data.counts ?? {};

  const onEdit = (roleKey: string) => {
    patchPortalSearch?.({ editRole: roleKey }, 'push');
  };

  const onBack = () => {
    patchPortalSearch?.({ editRole: null }, 'push');
  };

  const onSaved = (updated: RoleTemplate) => {
    setLocalTemplates((prev) => {
      const base = prev ?? templatesQuery.data?.data.roles ?? [];
      return base.map((t) => (t.role_key === updated.role_key ? updated : t));
    });
  };

  if (templatesQuery.isError) {
    return (
      <p className="roles-tab__error">
        {__('Impossible de charger les rôles. Réessayez plus tard.')}
      </p>
    );
  }

  if (templatesQuery.isLoading || templates === null) {
    return <p className="roles-tab__loading">{__('Chargement…')}</p>;
  }

  if (editingRoleKey !== null) {
    const template = templates.find((t) => t.role_key === editingRoleKey) ?? null;
    if (template === null) {
      return <p className="roles-tab__error">{__('Rôle introuvable.')}</p>;
    }
    return (
      <RolePermissionEditor
        template={template}
        bootstrap={bootstrap}
        permissionGate={templateEditGate}
        onBack={onBack}
        onSaved={onSaved}
      />
    );
  }

  return (
    <>
      <FeatureGateNotice decision={templateEditGate} />
      <RolesTable
        templates={templates}
        counts={counts}
        templateEditGate={templateEditGate}
        onEdit={onEdit}
      />
    </>
  );
};

export default CabinetRolesTab;
